<template>
  <div class="delivery-page">
    <!-- PAGE HERO -->
    <DeliveryHero
      :title="heading"
      subtitle="Fast shipping, reliable carriers, and transparent policies — so your order arrives exactly as expected."
    />

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <DeliverySidebar
        ref="sidebar"
        :sections="sections"
        :active-id="activeId"
        @navigate="scrollToSection"
      />

      <!-- Content -->
      <div class="policy-content">
        <!-- Where we ship -->
        <PolicySection
          class="reveal"
          section-id="where-we-ship"
          eyebrow="Coverage"
          title="Where we ship"
        >
          <p>
            We ship within the United States and internationally to the
            countries available at checkout.
          </p>
        </PolicySection>

        <!-- Processing time -->
        <PolicySection
          class="reveal"
          section-id="processing"
          eyebrow="Fulfillment"
          title="Processing time"
        >
          <p>
            All orders are processed and shipped same day or next business day
            after payment is received. Orders placed after 2 p.m. Eastern Time,
            on weekends, or on holidays begin processing the next business day.
          </p>
          <CalloutBox>
            <strong>Hours of operation:</strong> We ship Monday through Friday,
            excluding holidays.
          </CalloutBox>
        </PolicySection>

        <!-- Shipping methods -->
        <PolicySection
          class="reveal"
          section-id="methods"
          eyebrow="Shipping"
          title="Shipping methods &amp; delivery times"
        >
          <p>
            We partner with trusted carriers to get your order to you safely and
            on time.
          </p>
          <CarrierChips :carriers="carrierNames" />
          <ShippingTable :methods="shippingMethods" />
          <p>
            International shipping is $20 per order. Customs duties, import
            taxes, and any fees charged by the destination country are the
            buyer's responsibility.
          </p>
        </PolicySection>

        <!-- Tracking -->
        <PolicySection
          class="reveal"
          section-id="tracking"
          eyebrow="Order updates"
          title="Tracking your order"
        >
          <StepTimeline :steps="trackingSteps" />
          <CalloutBox variant="warning">
            <strong>Please note:</strong> We are unable to cancel an order once
            it has been shipped.
          </CalloutBox>
        </PolicySection>

        <!-- Customs & Duties -->
        <PolicySection
          class="reveal"
          section-id="customs"
          eyebrow="International orders"
          title="Customs &amp; duties"
        >
          <p>
            Prices on our site do not include any customs duties, taxes, or fees
            that may be imposed by your local government. These charges are the
            buyer's responsibility and are beyond our control.
          </p>
          <CalloutBox variant="warning">
            <strong>Heads up:</strong> We are not responsible if you are not
            notified by your country's postal authority that your order is
            awaiting customs payment.
          </CalloutBox>
        </PolicySection>

        <!-- Wrong Address -->
        <PolicySection
          class="reveal"
          section-id="address"
          eyebrow="Disclaimer"
          title="Wrong address"
        >
          <p>
            It is the buyer's responsibility to ensure the shipping address
            provided is correct. We are not liable for packages returned to us
            due to an incorrect or undeliverable address.
          </p>
          <p>
            If you need to update your shipping address, please contact us at
            <a href="mailto:support@hooliganseyewear.com" class="email-link"
              >support@hooliganseyewear.com</a
            >
            as soon as you place your order.
          </p>
        </PolicySection>

        <!-- Contact CTA -->
        <ContactCta class="reveal" />
      </div>
    </div>
  </div>
</template>

<script>
import "./delivery.css";

import DeliveryHero from "~/components/delivery/DeliveryHero.vue";
import DeliverySidebar from "~/components/delivery/DeliverySidebar.vue";
import PolicySection from "~/components/delivery/PolicySection.vue";
import ContactCta from "~/components/delivery/ContactCta.vue";
import CalloutBox from "~/components/ui/CalloutBox.vue";
import StepTimeline from "~/components/ui/StepTimeline.vue";
import ShippingTable from "~/components/ui/ShippingTable.vue";
import CarrierChips from "~/components/ui/CarrierChips.vue";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

export default {
  name: "DeliveryPage",
  setup() {
    return useStaticPageSeo({
      title: "Shipping Information | Hooligans Eyewear",
      heading: "Shipping Info",
      description:
        "Review Hooligans Eyewear shipping options, delivery estimates, order processing, tracking, international customs, and address policies.",
    });
  },
  components: {
    DeliveryHero,
    DeliverySidebar,
    PolicySection,
    ContactCta,
    CalloutBox,
    StepTimeline,
    ShippingTable,
    CarrierChips,
  },
  data() {
    return {
      activeId: "where-we-ship",
      sections: [
        { id: "where-we-ship", long: "Where we ship" },
        { id: "processing", long: "Processing time" },
        { id: "methods", long: "Shipping methods" },
        { id: "tracking", long: "Tracking your order" },
        { id: "customs", long: "Customs & duties" },
        { id: "address", long: "Wrong address" },
      ],
      carrierNames: ["USPS", "UPS", "FedEx"],
      shippingMethods: [
        {
          name: "U.S. Standard Shipping",
          cost: "Free",
          free: true,
          eta: "1–6 business days",
        },
        {
          name: "U.S. Express Shipping",
          cost: "$15",
          free: false,
          eta: "1–3 business days",
        },
        {
          name: "International Shipping",
          cost: "$20",
          free: false,
          eta: "7–20 business days, depending on destination",
        },
      ],
      trackingSteps: [
        {
          title: "Order placed",
          body: "Payment confirmed and your order enters our fulfillment queue.",
        },
        {
          title: "Shipped",
          body: "You'll receive a shipping confirmation email with your tracking number once your order is on its way.",
        },
        {
          title: "Track your package",
          body: "Use the tracking number to follow your order directly on the carrier's website.",
        },
        {
          title: "Delivered",
          body: "Your eyewear arrives at your door. Enjoy.",
        },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },
    _updateActive() {
      const els = this.sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x) => x.el);
      if (!els.length) return;

      let current = els[0].id;
      for (const { id, el } of els) {
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      if (current !== this.activeId) {
        this.activeId = current;
      }
    },
    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
    },
  },
};
</script>
